// 用于获取 pear 配置
// pear(计算平台前端组业务线)：https://pear-config.bigo.sg/public/index.html#/keyValueManagement/keyValue
// CDN: https://yun.likeevideo.com/file?path=%252Fas%252Fcommon-static%252Fbig-data
// npm: https://npm.ppx520.com/-/web/detail/@bigo/pear-fetch

/* eslint-disable no-return-await */
import * as Sentry from '@sentry/react';
import { REMOTE_FILE_URL } from 'constants/serviceUrl';
import { getRequest } from 'utils/request';
import { emitFingerprintSentry } from 'utils/sentry';
import { AxiosResponse } from 'axios';
import { CRM_URL_MAP } from 'constants/global';
import { SITE_ENV } from 'utils/global';
import { customFetch } from 'utils/customFetch';

const PEAR_I18N_ID = 10000001490;
// 数据字典id
const PEAR_DICT_ID = 10000001899;

const PEAR_ENV = ['dev', 'gray'].includes(SITE_ENV) || process.env.BRANCH_ENV === 'prod' ? 'gray-' : '';

/** 支持的 pear链接，按顺序重试，防止封禁  */
const PEAR_URLS = [
  'https://static-web.likee.com',
  'https://static-web.hzmk.site',
  'https://static-web.ukyuh.tech',
  'https://static-web.bigolive.tv',
];

async function getPear(fileUrl) {
  const pearUrls = [...PEAR_URLS];
  let url = '';
  let res = {} as AxiosResponse;

  async function retryPearFn(i = 0) {
    url = `${pearUrls[i]}${fileUrl}`;
    try {
      res = await rejectWithTimeout(() =>
        getRequest<AxiosResponse>(
          `${REMOTE_FILE_URL}?url=${encodeURIComponent(url)}`,
          {},
          {
            notFormat: true,
          }
        )
      );

      return res?.data;
    } catch (err) {
      if (i < PEAR_URLS.length - 1) {
        // 获取出错，可能是域名被封禁，递归重试
        return await retryPearFn(i + 1);
      }
      throw err;
    }
  }
  return await retryPearFn();
}

/** 获取多语言 */
export async function getI18n(lang: string) {
  try {
    const res = await getPear(`/as/common-static/pear/prod/${PEAR_ENV}${PEAR_I18N_ID}-${lang}.json?v=${Date.now()}`);
    return res;
  } catch (err) {
    emitFingerprintSentry({
      fingerprint: '[pear]获取pear多语言文件失败',
      message: '获取pear多语言文件失败',
      error: new Error(err),
    });
    console.error('获取pear字典值失败:', err);
    // 所有域名都失败了，获取本地文件
    const data = await tryLocalI18n(lang);
    return data;
  }
}

/** 获取pear数据字典 */
export async function getPearDictConfig() {
  try {
    const res = await getPear(`/as/common-static/pear/prod/${PEAR_ENV}${PEAR_DICT_ID}.json?v=${Date.now()}`);
    return res;
  } catch (err) {
    // 所有域名都失败了，获取本地文件
    const data = await tryLocalDicts();
    return data;
  }
}

// 如果远程加载失败了，尝试一下本地的多语言，并且上报记录
const TIME_STEMP = +new Date();
export async function tryLocalI18n(lng: string) {
  try {
    const res = await customFetch({ url: `/locales/${lng}.json?v=${TIME_STEMP}` });

    if (res.ok) {
      const jsonValue = await res.json();
      Sentry.captureMessage(`远程加载多语言失败 ${lng}，使用本地多语言文件`);
      return Promise.resolve(jsonValue);
    }

    Sentry.captureMessage(`远程加载多语言失败 ${lng} 且本地多语言也加载失败`);
    return Promise.reject(new Error('本地也请求失败了'));
  } catch (error) {
    Sentry.captureMessage(`远程加载多语言失败 ${lng} 且本地多语言也加载失败, ${error?.message}`);
    return Promise.reject(error);
  }
}

// 如果远程加载失败了，尝试一下本地的pear字典，并且上报记录
export async function tryLocalDicts() {
  try {
    const res = await customFetch({ url: `/pearData/pear-dicts.json?v=${TIME_STEMP}` });

    if (res.ok) {
      const jsonValue = await res.json();
      Sentry.captureMessage('远程加载pear字典值失败，使用本地pear字典文件');
      return Promise.resolve(jsonValue);
    }

    Sentry.captureMessage('远程加载pear字典值失败且本地pear字典也加载失败');
    return Promise.resolve({ crmUrlMap: CRM_URL_MAP });
  } catch (error) {
    Sentry.captureMessage(`远程加载pear字典值失败且本地pear字典也加载失败, ${error?.message}`);
    return Promise.resolve({ crmUrlMap: CRM_URL_MAP });
  }
}

/** 一段时间内请求没有返回就reject */
export function rejectWithTimeout(fn, timeout = 10000): Promise<AxiosResponse> {
  return new Promise((resolve, reject) => {
    // 设置定时器，用于取消请求
    const timeoutId = setTimeout(() => {
      reject(new Error('Request timeout.'));
    }, timeout);

    fn()
      .then((response) => {
        clearTimeout(timeoutId);
        resolve(response);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}
